import React from 'react';
import { MapPinIcon, PhoneIcon, ChevronRightIcon } from 'lucide-react';
import { MetaLabel } from '../components/MetaLabel';
import { useShoot } from '../contexts/ShootContext';

/**
 * Redesigned 9 Sep 2026 at Tom's request. This screen used to lead with the
 * live schedule status (Now/Next/Advance) and a three-up time grid, which
 * pushed location further down. Today is now just the static basics —
 * client, location, date — with "On the ground" (directions, the producer's
 * number, parking) surfaced right after them, since knowing how to get there
 * matters before knowing what's happening once you're there. The live
 * schedule status and the Advance control moved to the Schedule tab — "then
 * schedule and all that is just placed in the tab," in Tom's words.
 *
 * Second pass, same day: the first version put a full-width "Notes & Files"
 * row below "On the ground". Tom asked for it off the main screen entirely —
 * "we have the tabs at the bottom, need to keep it clean".
 *
 * Third pass, same day: this screen also used to end with a 2-up grid of
 * destination tiles (Schedule / Contacts / Shots / Kit) linking to the same
 * screens the bottom tab bar already reaches. Same "don't need it twice"
 * issue — "keep the tabs at the very bottom, that's cleaner" — so the grid is
 * gone; the bottom tabs are the only way to move between screens now.
 *
 * Fourth pass, same day: (1) The big title was the combined "Client —
 * Project" string, which repeated the client name shown a few lines below and
 * got cut off mid-word on a long name ("BLOOMBERG — Economy o..."). The client
 * now appears as its own line above the title, and the title shows only the
 * episode/day name. (2) The header's Notes & Files icon opened an internal
 * overlay holding only Important Instructions and Files; Tom asked for the
 * icon gone and Important Instructions shown directly on this screen, below
 * Parking. Files moved down here alongside it rather than becoming
 * unreachable. Both sections only render when there's something in them, and
 * neither ever appears on the client link. The orphaned overlay
 * (pages/Production.tsx) was left in place but is no longer wired up.
 *
 * Fifth pass, same day: Tom asked for the two header lines' type sizes
 * swapped, keeping the order — client large, the specific day quieter
 * underneath.
 *
 * 13 Sep 2026: the app is called Call Sheet now, not Shoot Day, and the line
 * under the title is the call sheet's own name for the day ("Live — Monday")
 * rather than the umbrella production's, since one production can now have
 * several days each with their own sheet.
 */
export function Today() {
  const {
    production,
    instructions,
    productionFiles
  } = useShoot();
  return <div className="pb-10" data-id="element-169" data-mp="yiwxpiavaw8e">
      <header className="px-5 pb-7 pt-6" data-id="element-170" data-mp="24xo4g2mo9bac">
        <MetaLabel tone="dim" data-id="element-171" data-mp="24wmhp0wan0cs">{production.studio} — Call Sheet</MetaLabel>

        <h1 className="mt-6 truncate text-[28px] font-light leading-[1.05] text-fg" style={{
        fontFamily: "'Jost', 'Helvetica Neue', Arial, sans-serif",
        letterSpacing: '-0.01em'
      }} data-id="element-172" data-mp="29w95u5dnu10d">
          {production.client || '—'}
        </h1>
        <MetaLabel tone="dim" as="div" className="mt-1" data-id="element-173" data-mp="3pht9pcv6czhp">
          {production.episodeTitle || production.title}
        </MetaLabel>

        <dl className="mt-7 border-t border-line" data-id="element-174" data-mp="3fq6n6kddzdzn">
          {[{
          label: 'Location',
          value: production.location
        }, {
          label: 'Date',
          value: production.date
        }].map(row => <div key={row.label} className="flex items-baseline justify-between border-b border-line py-3" data-id="element-175" data-mp="36op9t9rd5qen">
              <dt className="text-[10px] font-medium uppercase tracking-label text-dim" data-id="element-176" data-mp="31pchwalmxt57">{row.label}</dt>
              <dd className="text-[15px] text-fg" data-id="element-177" data-mp="2ws456pj9ahbc">{row.value || '—'}</dd>
            </div>)}
        </dl>
      </header>

      <div className="px-5" data-id="element-178" data-mp="zxzlyb1navla">
        <MetaLabel tone="dim" data-id="element-179" data-mp="uzoci1f9in3l">On the ground</MetaLabel>
        <div className="mt-3 border-t border-line" data-id="element-180" data-mp="v07koqf41x4f">
          {production.location && <a href={production.locationMapUrl || `https://maps.google.com/?q=${encodeURIComponent(production.address || production.location)}`} target="_blank" rel="noreferrer" className="flex h-16 items-center gap-4 border-b border-line text-fg transition-colors duration-150 ease-out hover:bg-surface" data-id="element-181" data-mp="131p029p46kq2">
              <MapPinIcon className="h-[18px] w-[18px] shrink-0 text-muted" strokeWidth={1.5} data-id="element-182" data-mp="k8kzs5he59mh" />
              <span className="flex-1 text-[15px]" data-id="element-183" data-mp="p1xg7sbx2a45">Open Location</span>
              <span className="max-w-[55%] truncate text-[11px] text-dim" data-id="element-184" data-mp="acrh04d2wjw9">
                {production.address || production.location}
              </span>
              <ChevronRightIcon className="h-4 w-4 text-dim" strokeWidth={1.5} data-id="element-185" data-mp="1b0bazijgd2e" />
            </a>}
          {production.producer && <a href={`tel:${production.producer.phone.replace(/\s/g, '')}`} className="flex h-16 items-center gap-4 border-b border-line text-fg transition-colors duration-150 ease-out hover:bg-surface" data-id="element-186" data-mp="5bbvkb6zobdc">
              <PhoneIcon className="h-[18px] w-[18px] shrink-0 text-muted" strokeWidth={1.5} data-id="element-187" data-mp="3dwz1tqxh2b9w" />
              <span className="flex-1 text-[15px]" data-id="element-188" data-mp="3rs14m570vkih">Call Producer</span>
              <span className="text-[11px] text-dim" data-id="element-189" data-mp="3mtr9fulasjwv">{production.producer.name}</span>
              <ChevronRightIcon className="h-4 w-4 text-dim" strokeWidth={1.5} data-id="element-190" data-mp="5lw4kfu69ilw" />
            </a>}
          {production.parkingNotes && <div className="border-b border-line py-4" data-id="element-191" data-mp="efhjbz6c6zvb">
              <span className="text-[10px] font-medium uppercase tracking-label text-dim" data-id="element-192" data-mp="eezewf9itfcb">Parking</span>
              <p className="mt-1 text-[14px] leading-relaxed text-fg" data-id="element-193" data-mp="aefhdal1c9pn">{production.parkingNotes}</p>
            </div>}
        </div>
      </div>

      {instructions.length > 0 && <div className="mt-10 px-5" data-id="element-194" data-mp="jfwt6gretp8j">
          <MetaLabel tone="dim" data-id="element-195" data-mp="jcr6t9a611gi">Important Instructions</MetaLabel>
          <ul className="mt-3 border-t border-line" data-id="element-196" data-mp="3d84wsu96th2w">
            {instructions.map((line, index) => <li key={line} className="flex gap-4 border-b border-line py-4" data-id="element-197" data-mp="3h8ox26p250t9" data-mpi={index}>
                <span className="shrink-0 pt-0.5 text-[10px] tabular-nums tracking-label text-dim" data-id="element-198" data-mp="3m672vo7s2zx1">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="text-[14px] leading-relaxed text-fg" data-id="element-199" data-mp="3vxl2dvzqbzq6">{line}</span>
              </li>)}
          </ul>
        </div>}

      {productionFiles.length > 0 && <div className="mt-10 px-5" data-id="element-200" data-mp="8ru17b6oggoo">
          <MetaLabel tone="dim" data-id="element-201" data-mp="8tn6am1n6suk">Files</MetaLabel>
          <div className="mt-3 border-t border-line" data-id="element-202" data-mp="drgh5sdw4b16">
            {productionFiles.map(file => file.url ? <a key={file.id} href={file.url} target="_blank" rel="noreferrer" className="flex h-16 items-center gap-4 border-b border-line text-fg transition-colors duration-150 ease-out hover:bg-surface" data-id="element-203" data-mp="2aki4fkjbn44g">
                  <span className="flex-1 text-[15px]" data-id="element-204" data-mp="237si82l2rucr">{file.title}</span>
                  <span className="text-[11px] text-dim" data-id="element-205" data-mp="1y8pm57n2oygx">{file.meta}</span>
                  <ChevronRightIcon className="h-4 w-4 text-dim" strokeWidth={1.5} data-id="element-206" data-mp="1tae8eltnq3tf" />
                </a> : <div key={file.id} className="flex h-16 items-center gap-4 border-b border-line text-fg" data-id="element-207" data-mp="2qyep3qjcf8l6">
                  <span className="flex-1 text-[15px]" data-id="element-208" data-mp="2m03cx95kslzs">{file.title}</span>
                  <span className="text-[11px] text-dim" data-id="element-209" data-mp="2gzp4vr3z5c1r">{file.meta}</span>
                </div>)}
          </div>
        </div>}
    </div>;
}